import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import ReactDOM from 'react-dom';

import './DropTargetForGrid.scss';

const propTypes = {

	/**
	 * The drag-and-drop type of the drop target.
	 */
	type: PropTypes.string.isRequired,

	/**
	 * The current item related to this drop target.
	 */
	currentItem: PropTypes.shape({

		/**
		 * The unique ID of the item.
		 */
		id: PropTypes.string,

		/**
		 * The width of the item.
		 */
		width: PropTypes.number,

	}),

	/**
	 * The previous item (if any).
	 */
	prevItem: PropTypes.shape({

		/**
		 * The unique ID of the item.
		 */
		id: PropTypes.string,

		/**
		 * The width of the item.
		 */
		width: PropTypes.number,

	}),

	/**
	 * The next item (if any).
	 */
	nextItem: PropTypes.shape({

		/**
		 * The unique ID of the item.
		 */
		id: PropTypes.string,

		/**
		 * The width of the item.
		 */
		width: PropTypes.number,

	}),

	/**
	 * The row of items that contains the current item.
	 */
	currentRow: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The unique ID of the item.
		 */
		id: PropTypes.string,

		/**
		 * The width of the item.
		 */
		width: PropTypes.number,

	})),

	/**
	 * The row of items that is placed before the current row (if any).
	 */
	prevRow: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The unique ID of the item.
		 */
		id: PropTypes.string,

		/**
		 * The width of the item.
		 */
		width: PropTypes.number,

	})),

	/**
	 * The row of items that is placed after the current row (if any).
	 */
	nextRow: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The unique ID of the item.
		 */
		id: PropTypes.string,

		/**
		 * The width of the item.
		 */
		width: PropTypes.number,

	})),

	/**
	 * Any extra positioning info related to the current item and the current row.
	 */
	positioning: PropTypes.shape({

		/**
		 * Flag indicating if the current row is the first row.
		 */
		firstRow: PropTypes.bool,

		/**
		 * Flag indicating if the current row is the last row.
		 */
		lastRow: PropTypes.bool,

		/**
		 * Flag indicating if the current item is the first item in the current row.
		 */
		firstInRow: PropTypes.bool,

		/**
		 * Flag indicating if the current item is the last item in the current row.
		 */
		lastInRow: PropTypes.bool,

		/**
		 * Flag indicating if the current item is filling empty space at the end of the current row.
		 */
		isEmptySpace: PropTypes.bool,

		/**
		 * The id of the DOM element for use in displaying the top and bottom drop target locations.
		 */
		dropTargetForRowID: PropTypes.string.isRequired,

	}),

};

/**
 * Shared component to render a Drop Target for an arbitrary M by N grid of items, using react-dnd.
 *
 * @param   {Object} props  The props.
 * @returns {Object}        The rendered component.
 */
const DropTargetForGrid = (props) => {

	const {
		type,
		currentItem,
		prevItem,
		nextItem,
		currentRow,
		prevRow,
		nextRow,
		positioning
	} = props;

	const targetRef = useRef();

	// Keep track of the current hover status for each possible location for this drop target
	const [hoverStatus, setHoverStatus] = useState({
		top: false,
		right: false,
		bottom: false,
		left: false,
	});

	const itemsAndRows = {
		currentItem,
		prevItem,
		nextItem,
		currentRow,
		prevRow,
		nextRow,
	};

	const [collectedDndProps, connectDropTarget] = useDrop({
		accept: type,
		drop: () => {

			const moveToTop = hoverStatus.top;
			const moveToBottom = hoverStatus.bottom;

			let moveToRight = hoverStatus.right;

			let moveToLeft = hoverStatus.left;

			// Check if the drop target is filling empty space at the end of the current row

			// In that case, we need to flip the left and right drop target locations so that it can
			// match the behavior of the drop target locations for the last item in the current row
			if (positioning.isEmptySpace) {

				moveToRight = moveToLeft;
				moveToLeft = false;

			}

			// Check if dropped to the left
			if (moveToLeft) {

				// Place the item b/w the previous item and the current item
				return {
					prevItem,
					nextItem: currentItem,
				};

			// Check if dropped to the right
			} else if (moveToRight) {

				// Place the item b/w the current item and the next item
				return {
					prevItem: currentItem,
					nextItem,
				};

			// Check if dropped above
			} else if (moveToTop) {

				// Place the item b/w the previous row and the current row
				return {
					prevItem: prevRow && prevRow[prevRow.length - 1],
					nextItem: currentRow[0],
				};

			// Check if dropped below
			} else if (moveToBottom) {

				// Place the item b/w the current row and the next row
				return {
					prevItem: currentRow[currentRow.length - 1],
					nextItem: nextRow && nextRow[0],
				};

			}

			// Otherwise, don't do anything :)
			return undefined;

		},
		hover: (draggedItem, monitor) => {

			// Calculate which of the drop target locations are valid (if any)
			const validLocations = validTargetLocations(draggedItem, itemsAndRows, positioning);

			// Re-calculate the new hover status for each location of this drop target
			const updatedHoverStatus = calculateHoverLocation(targetRef, monitor, validLocations);

			// Force the component to re-render to indicate the updated hover status
			setHoverStatus(updatedHoverStatus);

		},
		collect: (monitor) => {
			return {
				isOver: monitor.isOver(),
				draggedItem: monitor.getItemType() === type ? monitor.getItem() : null,
			};

		},
	});

	const draggedItem = collectedDndProps.draggedItem;
	const isOver = collectedDndProps.isOver;

	// Calculate the valid drop target locations (if any)
	const validLocations = validTargetLocations(draggedItem, itemsAndRows, positioning);

	const isDragging = draggedItem ? 'tc-grid-is-dragging' : '';
	const isDraggingOver = isOver ? 'tc-grid-is-over' : '';

	// Check if a valid drop target location is hovered over by the dragged item
	const isHoverTop = isOver && hoverStatus.top ? 'tc-grid-drop-target-hover-top' : '';
	const isHoverRight = isOver && hoverStatus.right ? 'tc-grid-drop-target-hover-right' : '';
	const isHoverBottom = isOver && hoverStatus.bottom ? 'tc-grid-drop-target-hover-bottom' : '';
	const isHoverLeft = isOver && hoverStatus.left ? 'tc-grid-drop-target-hover-left' : '';

	// Determine the required style based on the drop target's position in the grid layout
	let position = '';

	// Check if the current row is the first row
	if (positioning.firstRow) {
		position += 'tc-grid-first-row ';
	}

	// Check if the current row is the last row
	if (positioning.lastRow) {
		position += 'tc-grid-last-row ';
	}

	// Check if the current item is the first item in the row
	if (positioning.firstInRow) {
		position += 'tc-grid-first-in-row ';
	}

	// Check if the current item is the last item in the row
	if (positioning.lastInRow) {
		position += 'tc-grid-last-in-row ';
	}

	// Check if the current item is filling empty space at the end of the row
	if (positioning.isEmptySpace) {
		position += 'tc-grid-is-empty-space ';
	}

	return (
		<div
			ref={connectDropTarget(targetRef)}
			className={`tc-grid-drop-target ${isDragging} ${isDraggingOver} ${position}`}>
			{validLocations.top && targetRef && ReactDOM.createPortal(
				<div
					// eslint-disable-next-line max-len
					className={`tc-grid-drop-target-location tc-grid-droppable-top ${position} ${isHoverTop}`}>
				</div>,
				document.getElementById(positioning.dropTargetForRowID)
			)}
			{validLocations.right &&
				<div
					// eslint-disable-next-line max-len
					className={`tc-grid-drop-target-location tc-grid-droppable-right ${position} ${isHoverRight}`}>
				</div>
			}
			{validLocations.bottom && targetRef && ReactDOM.createPortal(
				<div
					// eslint-disable-next-line max-len
					className={`tc-grid-drop-target-location tc-grid-droppable-bottom ${position} ${isHoverBottom}`}>
				</div>,
				document.getElementById(positioning.dropTargetForRowID)
			)}
			{validLocations.left &&
				<div
					// eslint-disable-next-line max-len
					className={`tc-grid-drop-target-location tc-grid-droppable-left ${position} ${isHoverLeft}`}>
				</div>
			}
		</div>
	);

};

DropTargetForGrid.propTypes = propTypes;

export default DropTargetForGrid;

/**
 * Calculates the total width of a row given its list of items.
 *
 * @param {array} row  The list of items in the row.
 *
 * @returns {number} The total width of a row (0 - 12)
 */
function calculateRowWidth(row) {

	return row.reduce((acc, item) => acc + item.width, 0);

}

/**
 * Calculate the hover status for each location of this drop target to determine if any are active.
 *
 * @param {object} node                 The React node (via Ref) for the drop target.
 * @param {object} monitor              The current drag-and-drop state, provided by react-dnd.
 *
 * @param {object} validLocations       The valid locations for this drop target given
 *                                      the item being dragged.
 * @param {bool} validLocations.top     True, if the top side is a valid drop location.
 * @param {bool} validLocations.right   True, if the right side is a valid drop location.
 * @param {bool} validLocations.bottom  True, if the bottom side is a valid drop location.
 * @param {bool} validLocations.left    True, if the left side is a valid drop location.
 *
 * @returns {object} result       The hover status result.
 * @returns {bool} result.top     True, if the top side is a valid location and is hovered over.
 * @returns {bool} result.right   True, if the right side is a valid location and is hovered over.
 * @returns {bool} result.bottom  True, if the bottom side is a valid location and is hovered over.
 * @returns {bool} result.left    True, if the left side is a valid location and is hovered over.
 */
function calculateHoverLocation(node, monitor, validLocations) {

	const result = {
		top: false,
		right: false,
		bottom: false,
		left: false,
	};

	// Determine the drop target xy coordinates on screen
	const hoverBoundingRect = node.current.getBoundingClientRect();

	// Determine the mouse position xy coordinates on screen
	const clientOffset = monitor.getClientOffset();

	// Determine the width and height of the drop target
	const width = hoverBoundingRect.right - hoverBoundingRect.left;
	const height = hoverBoundingRect.bottom - hoverBoundingRect.top;

	// Determine the distance from the mouse position to the drop target locations
	const distToTop = clientOffset.y - hoverBoundingRect.top;
	const distToLeft = clientOffset.x - hoverBoundingRect.left;
	const distToRight = hoverBoundingRect.right - clientOffset.x;
	const distToBottom = hoverBoundingRect.bottom - clientOffset.y;

	const validOptions = [];

	if (validLocations.top) {
		validOptions.push(distToTop);
	}

	if (validLocations.right) {
		validOptions.push(distToRight);
	}

	if (validLocations.bottom) {
		validOptions.push(distToBottom);
	}

	if (validLocations.left) {
		validOptions.push(distToLeft);
	}

	// Pick the closest of the valid drop target locations
	const min = Math.min(...validOptions);

	// Determine which drop target location is active (if any)
	if (min === distToTop && validLocations.top && distToTop < height / 2) {

		result.top = true;

	} else if (min === distToRight && validLocations.right && distToRight < width / 2) {

		result.right = true;

	} else if (min === distToBottom && validLocations.bottom && distToBottom < height / 2) {

		result.bottom = true;

	} else if (min === distToLeft && validLocations.left && distToLeft < width / 2) {

		result.left = true;

	}

	return result;

}

/**
 * Calculates the valid drop target locations given the current item and the dragged item.
 *
 * @param {object} draggedItem               The item being dragged, provided by react-dnd.
 *
 * @param {object} itemsAndRows              The relevant items and rows in the grid related
 *                                           to this drop target.
 * @param {object} itemsAndRows.currentItem  The current item related to this drop target.
 * @param {object} itemsAndRows.prevItem     The item that comes before the current item.
 * @param {object} itemsAndRows.nextItem     The item that goes after the current item.
 * @param {object} itemsAndRows.currentRow   The current row related to this drop target.
 * @param {object} itemsAndRows.prevRow      The row that comes before the current row.
 * @param {object} itemsAndRows.nextRow      The row that goes after the current row.
 *
 * @param {object} positioning               Any extra positioning info related to the current
 *                                           item and the current row.
 * @param {bool} positioning.isEmptySpace    Flag indicating if the current item is filling empty
 *                                           space at the end of the current row.
 *
 * @returns {object} result         The drop target locations and respective validity status.
 * @returns {bool}   result.top     Flag indicating if the "top" drop target location is valid.
 * @returns {bool}   result.right   Flag indicating if the "right" drop target location is valid.
 * @returns {bool}   result.bottom  Flag indicating if the "bottom" drop target location is valid.
 * @returns {bool}   result.left    Flag indicating if the "left" drop target location is valid.
 */
function validTargetLocations(draggedItem, itemsAndRows, positioning) {

	const result = {
		top: false,
		right: false,
		bottom: false,
		left: false,
	};

	const { currentItem	} = itemsAndRows;

	if (draggedItem && draggedItem.data && draggedItem.data.id !== currentItem.id) {

		// Determine which of the 4 drop target locations are possible based on the dragged item
		result.top = canFitTop(draggedItem.data, itemsAndRows);
		result.right = canFitRight(draggedItem.data, itemsAndRows);
		result.bottom = canFitBottom(draggedItem.data, itemsAndRows);
		result.left = canFitLeft(draggedItem.data, itemsAndRows);

		// Now, check if the drop target is filling empty space at the end of the current row

		// In that case, we need to flip the left and right drop target locations so that it matches
		// the drop target locations for the last item in the current row

		if (positioning.isEmptySpace) {

			// Dropping after the current item, but show the left drop target location
			result.left = result.right;
			// Dropping after the current item, but hide the right drop target location
			result.right = false;

		}

	}

	return result;

}

/**
 * Checks if the dragged item can fit above the current item,
 * i.e. in between the previous row and the current row.
 *
 * @param {object} draggedItem               The item being dragged, provided by react-dnd.
 *
 * @param {object} itemsAndRows              The relevant items and rows in the grid related
 *                                           to this drop target.
 * @param {object} itemsAndRows.currentItem  The current item related to this drop target.
 * @param {object} itemsAndRows.prevItem     The item that comes before the current item.
 * @param {object} itemsAndRows.nextItem     The item that goes after the current item.
 * @param {object} itemsAndRows.currentRow   The current row related to this drop target.
 * @param {object} itemsAndRows.prevRow      The row that comes before the current row.
 * @param {object} itemsAndRows.nextRow      The row that goes after the current row.
 *
 * @returns {boolean}  True, if it can fit.
 */
function canFitTop(draggedItem, itemsAndRows) {

	const { currentRow, prevRow } = itemsAndRows;

	// If the dragged item is in the previous row, then it can't fit by default
	if (prevRow && prevRow.findIndex((item) => item.id === draggedItem.id) >= 0) {
		return false;
	}

	// If the dragged item is in the current row, then it can't fit by default
	if (currentRow.findIndex((item) => item.id === draggedItem.id) >= 0) {
		return false;
	}

	// Now, check if the item can fit in between the current row and the previous row (if it exists)

	const canFitAboveCurrentRow = draggedItem.width + currentRow[0].width > 12;

	const canFitBelowPrevRow = !prevRow || draggedItem.width + calculateRowWidth(prevRow) > 12;

	return canFitAboveCurrentRow && canFitBelowPrevRow;

}

/**
 * Checks if the dragged item can fit to the right of the current item,
 * i.e. in between the current item and the next item.
 *
 * @param {object} draggedItem               The item being dragged, provided by react-dnd.
 *
 * @param {object} itemsAndRows              The relevant items and rows in the grid related
 *                                           to this drop target.
 * @param {object} itemsAndRows.currentItem  The current item related to this drop target.
 * @param {object} itemsAndRows.prevItem     The item that comes before the current item.
 * @param {object} itemsAndRows.nextItem     The item that goes after the current item.
 * @param {object} itemsAndRows.currentRow   The current row related to this drop target.
 * @param {object} itemsAndRows.prevRow      The row that comes before the current row.
 * @param {object} itemsAndRows.nextRow      The row that goes after the current row.
 *
 * @returns {boolean}  True, if it can fit.
 */
function canFitRight(draggedItem, itemsAndRows) {

	const { currentItem, nextItem, currentRow } = itemsAndRows;

	// Check that the item to the right isn't the dragged item itself
	if (nextItem && nextItem.id === draggedItem.id) {
		return false;
	}

	// Get the index of the current item in the current row
	const index = currentRow.findIndex((item) => item.id === currentItem.id);

	// Get the items in the current row up to, and including the current item
	let rowSlice = currentRow.slice(0, index + 1);

	// Remove the dragged item (if it exists)
	rowSlice = rowSlice.filter((item) => item.id !== draggedItem.id);

	// Check if the item can fit after the current item in the row
	if (draggedItem.width + calculateRowWidth(rowSlice) <= 12) {
		return true;
	}

	return false;

}

/**
 * Checks if the dragged item can fit below the current item,
 * i.e. in between the current row and the next row.
 *
 * @param {object} draggedItem               The item being dragged, provided by react-dnd.
 *
 * @param {object} itemsAndRows              The relevant items and rows in the grid related
 *                                           to this drop target.
 * @param {object} itemsAndRows.currentItem  The current item related to this drop target.
 * @param {object} itemsAndRows.prevItem     The item that comes before the current item.
 * @param {object} itemsAndRows.nextItem     The item that goes after the current item.
 * @param {object} itemsAndRows.currentRow   The current row related to this drop target.
 * @param {object} itemsAndRows.prevRow      The row that comes before the current row.
 * @param {object} itemsAndRows.nextRow      The row that goes after the current row.
 *
 * @returns {boolean}  True, if it can fit.
 */
function canFitBottom(draggedItem, itemsAndRows) {

	const {	currentRow,	nextRow } = itemsAndRows;

	// If the dragged item is in the current row, then it can't fit by default
	if (currentRow.findIndex((item) => item.id === draggedItem.id) >= 0) {
		return false;
	}

	// If the dragged item is in the next row, then it can't fit by default
	if (nextRow && nextRow.findIndex((item) => item.id === draggedItem.id) >= 0) {
		return false;
	}

	// Now, check if the item can fit in between the current row and the next row (if it exists)

	const canFitBelowCurrentRow = draggedItem.width + calculateRowWidth(currentRow) > 12;

	const canFitAboveNextRow = !nextRow || draggedItem.width + nextRow[0].width > 12;

	return canFitBelowCurrentRow && canFitAboveNextRow;

}

/**
 * Checks if the dragged item can fit to the left of the current item,
 * i.e. in between the previous item and the current item.
 *
 * @param {object} draggedItem               The item being dragged, provided by react-dnd.
 *
 * @param {object} itemsAndRows              The relevant items and rows in the grid related
 *                                           to this drop target.
 * @param {object} itemsAndRows.currentItem  The current item related to this drop target.
 * @param {object} itemsAndRows.prevItem     The item that comes before the current item.
 * @param {object} itemsAndRows.nextItem     The item that goes after the current item.
 * @param {object} itemsAndRows.currentRow   The current row related to this drop target.
 * @param {object} itemsAndRows.prevRow      The row that comes before the current row.
 * @param {object} itemsAndRows.nextRow      The row that goes after the current row.
 *
 * @returns {boolean}  True, if it can fit.
 */
function canFitLeft(draggedItem, itemsAndRows) {

	const {	currentItem, prevItem, currentRow } = itemsAndRows;

	// Check that the item to the left isn't the dragged item itself
	if (prevItem && prevItem.id === draggedItem.id) {
		return false;
	}

	// Get the index of the current item in the current row
	const index = currentRow.findIndex((item) => item.id === currentItem.id);

	// Check if the current item is the first item in the current row
	if (index === 0) {

		// Check if the item can fit before the current item
		if (draggedItem.width + currentItem.width <= 12) {
			return true;
		}

	} else {

		// Get the items in the current row up to, but excluding the current item
		let rowSlice = currentRow.slice(0, index);

		// Remove the dragged item (if it exists)
		rowSlice = rowSlice.filter((item) => item.id !== draggedItem.id);

		// Check if the item can fit before the current item in the current row
		if (draggedItem.width + calculateRowWidth(rowSlice) <= 12) {
			return true;
		}

	}

	return false;

}
